// apps/share-web/src/song-screen/mixer-pane.ts
//
// Микшер телефонного экрана: строки дорожек, Master, строка состояния, меню
// дорожки (долгое нажатие) и ⋯ микшера (Only my part / Band without me /
// Change my part / Reset mix — решение Сергея 16.10: не постоянная строка, а меню).
//
// Владелец желаемого микса — store. Движок получает его через setTrackState,
// а дорожка, собранная позже, — по своему load-progress (player.ts теряет вызовы
// для ещё не собранных дорожек, :590-593). Так пресет, нажатый во время загрузки,
// не теряется на поздних дорожках — без правки движка.
//
// Монтирование НЕ затирает движок: экран пересобирается при повороте через порог
// 768 px, а плеер при этом живёт дальше. Поэтому на монтировании в движок уходят
// только собранные дорожки, чьё состояние там отличается от стора, а Master не
// трогается вовсе (он уходит только при смене). И дальше в движок идёт лишь то, что
// изменилось по ЗНАЧЕНИЯМ: каждый setTrackState пересчитывает все гейны и будит
// `mixchanged`.
import type { ShareManifest } from '@bandway/share-types';
import type { Player } from '../player.js';
import type { SongScreenStore, SongScreenState, MyPartMode } from './song-screen-store.js';
import { mountMixRow, type MixRowHandle } from './mix-row.js';
import { openActionSheet, closeActionSheet } from './action-sheet.js';
import { onlyTrack, withoutTrack, clearFocus, resetMix, findTrackByName, type TrackMix } from './mix-presets.js';
import { iconButton } from '../chart/chart-buttons.js';
import { t } from '../ui/i18n.js';
import '../styles/song-screen.css';

type Track = ShareManifest['tracks'][number];
export type MixerPlayer = Pick<Player, 'setTrackState' | 'setMasterGain' | 'subscribe' | 'getTrackVuLevel' | 'getTrackStates'>;

export interface MixerPaneDeps { tracks: Track[]; player: MixerPlayer; store: SongScreenStore; }
export interface MixerPaneHandle { tick(): void; openMenu(): void; destroy(): void; }

const MASTER_MAX = 4;   // тот же потолок, что у player.setMasterGain

const sameMix = (a: TrackMix, b: TrackMix): boolean => a.gain === b.gain && a.muted === b.muted && a.soloed === b.soloed;

export function mountMixerPane(host: Element, d: MixerPaneDeps): MixerPaneHandle {
  const { tracks, player, store } = d;
  const root = document.createElement('div');
  root.className = 'ss-mixer';
  const status = document.createElement('div');
  status.className = 'ss-status';
  status.setAttribute('role', 'status');
  status.hidden = true;
  const list = document.createElement('div');
  list.className = 'ss-rows';
  root.appendChild(status);
  root.appendChild(list);
  host.appendChild(root);

  const loaded = new Set<string>(typeof player.getTrackStates === 'function' ? [...player.getTrackStates().keys()] : []);
  const nameOf = (id: string): string => tracks.find((tr) => tr.id === id)?.name ?? '';
  const myPartId = (s: SongScreenState): string | null => findTrackByName(tracks, s.myPartName);

  const setMix = (mix: Record<string, TrackMix>, myPartMode: MyPartMode = null): void => store.set({ mix, myPartMode });
  /** Микс после включения (`mode`) или снятия (`null`) «моей партии» `id` — чистая функция, без записи в стор. */
  const mixForMyPart = (s: SongScreenState, id: string, mode: MyPartMode): Record<string, TrackMix> => {
    const base = clearFocus(s.mix, s.myPartMode === 'without' ? id : null);
    return mode === 'only' ? onlyTrack(base, id) : mode === 'without' ? withoutTrack(base, id) : base;
  };
  const setGain = (id: string, gain: number): void => {
    const s = store.get();
    store.set({ mix: { ...s.mix, [id]: { ...s.mix[id], gain } } });          // уровень не сбрасывает «мою партию»
  };
  const setFlag = (id: string, p: Partial<TrackMix>): void => {
    const s = store.get();
    setMix({ ...s.mix, [id]: { ...s.mix[id], ...p } });                        // M/S руками — режим «моей партии» снят
  };

  const rows = new Map<string, MixRowHandle>();
  for (const tr of tracks) {
    const m = store.get().mix[tr.id];
    rows.set(tr.id, mountMixRow(list, {
      name: tr.name,
      color: tr.color,
      factor: m.gain,
      muted: m.muted,
      soloed: m.soloed,
      state: loaded.has(tr.id) ? 'ready' : 'loading',
      onLevel: (f) => setGain(tr.id, f),
      onMute: (next) => setFlag(tr.id, { muted: next }),
      onSolo: (next) => setFlag(tr.id, { soloed: next }),
      onMenu: (x, y) => openTrackMenu(tr, x, y),
    }));
  }
  const master = mountMixRow(list, {
    name: t('songScreen.master'),
    color: 'var(--fg-3)',
    factor: store.get().master,
    master: true,
    maxFactor: MASTER_MAX,
    onLevel: (f) => store.set({ master: f }),
  });

  const push = (id: string): void => {
    const m = store.get().mix[id];
    if (m) player.setTrackState(id, { gain: m.gain, muted: m.muted, soloed: m.soloed });
  };

  // Строка состояния пересобирается только при смене текста: уровень двигается
  // десятки раз в секунду, а новый <bw-button> на каждое движение — лишний рендер
  // Lit-компонента на телефоне (и ✕ не должен исчезать из-под пальца).
  let statusText = '';
  const renderStatus = (s: SongScreenState): void => {
    const id = myPartId(s);
    let text = '';
    if (s.myPartMode === 'only' && id) text = t('songScreen.statusOnly', { name: nameOf(id) });
    else if (s.myPartMode === 'without' && id) text = t('songScreen.statusWithout', { name: nameOf(id) });
    else {
      const solos = tracks.filter((tr) => s.mix[tr.id]?.soloed).map((tr) => tr.name);
      if (solos.length) text = t('songScreen.statusSolo', { names: solos.join(', ') });
    }
    status.hidden = !text;
    if (text === statusText) return;
    statusText = text;
    status.textContent = '';
    if (!text) return;
    const span = document.createElement('span');
    span.textContent = text;
    status.appendChild(span);
    status.appendChild(iconButton({
      glyph: '✕',
      name: t('songScreen.clearFocus'),
      size: 'md',
      onClick: () => {
        const cur = store.get();
        setMix(clearFocus(cur.mix, cur.myPartMode === 'without' ? myPartId(cur) : null));
      },
    }));
  };

  const renderRows = (s: SongScreenState): void => {
    const anySolo = Object.values(s.mix).some((m) => m.soloed);
    for (const [id, r] of rows) {
      const m = s.mix[id];
      r.update({ factor: m.gain, muted: m.muted, soloed: m.soloed, dimmed: anySolo && !m.soloed, state: loaded.has(id) ? 'ready' : 'loading' });
    }
    master.update({ factor: s.master });
    renderStatus(s);
  };

  const unsubStore = store.subscribe((s, prev) => {
    for (const id of Object.keys(s.mix)) {
      const before = prev.mix[id];
      if (!before || !sameMix(s.mix[id], before)) push(id);          // по значениям: новый объект с теми же числами — не повод будить движок
    }
    if (s.master !== prev.master) player.setMasterGain(Math.min(MASTER_MAX, s.master));
    renderRows(s);
  });
  const unsubPlayer = player.subscribe((ev) => {
    if (ev.type !== 'load-progress') return;
    loaded.add(ev.trackId);
    push(ev.trackId);
    renderRows(store.get());
  });

  // Монтирование: только собранные дорожки, чьё состояние в движке иное. Несобранные
  // движок всё равно потерял бы — их досылает load-progress. Master не пушим.
  if (typeof player.getTrackStates === 'function') {
    const engine = player.getTrackStates();
    for (const tr of tracks) {
      const es = engine.get(tr.id);
      const want = store.get().mix[tr.id];
      if (es && want && !sameMix(es, want)) push(tr.id);
    }
  }
  renderRows(store.get());

  // «Только эта» / «без этой» дорожки: считается по стору В МОМЕНТ ВЫБОРА — пока меню
  // было открыто, микс мог измениться (нажали S, пришёл пресет).
  const trackFocus = (s: SongScreenState, id: string): { isOnly: boolean; isWithout: boolean } => {
    const solos = Object.keys(s.mix).filter((k) => s.mix[k].soloed);
    return { isOnly: solos.length === 1 && solos[0] === id, isWithout: !!s.mix[id]?.muted && solos.length === 0 };
  };

  function openTrackMenu(tr: Track, x: number, y: number): void {
    const shown = trackFocus(store.get(), tr.id);                    // только для галочек
    openActionSheet({
      at: { x, y },
      title: tr.name,
      items: [
        { label: t('songScreen.onlyThis'), checked: shown.isOnly, onSelect: () => {
          const cur = store.get();
          setMix(trackFocus(cur, tr.id).isOnly ? clearFocus(cur.mix, null) : onlyTrack(cur.mix, tr.id));
        } },
        { label: t('songScreen.withoutThis'), checked: shown.isWithout, onSelect: () => {
          const cur = store.get();
          setMix(trackFocus(cur, tr.id).isWithout ? clearFocus(cur.mix, tr.id) : withoutTrack(cur.mix, tr.id));
        } },
        { label: t('songScreen.resetTrack'), onSelect: () => setGain(tr.id, 1) },
      ],
    });
  }

  const applyMyPart = (mode: MyPartMode): void => {
    const s = store.get();
    const id = myPartId(s);
    if (!id) return;
    setMix(mixForMyPart(s, id, mode), mode);
  };

  const openPartPicker = (): void => {
    const s = store.get();
    const cur = myPartId(s);
    openActionSheet({
      title: t('songScreen.myPartTitle'),
      items: tracks.map((tr) => ({
        label: tr.name,
        checked: cur === tr.id,
        onSelect: () => {
          const now = store.get();
          const mode = now.myPartMode;
          if (!mode) { store.set({ myPartName: tr.name }); return; }
          // Режим активен: прежняя партия снимается, тот же режим ложится на новую — ОДНОЙ
          // записью. Три записи подряд слали бы в движок промежуточный «все соло сняты».
          const oldId = myPartId(now);
          const cleared = oldId ? mixForMyPart(now, oldId, null) : now.mix;
          const mix = mixForMyPart({ ...now, mix: cleared, myPartMode: null }, tr.id, mode);
          store.set({ mix, myPartMode: mode, myPartName: tr.name });
        },
      })),
    });
  };

  const confirmReset = (): void => {
    openActionSheet({
      title: t('songScreen.resetMixConfirm'),
      items: [{ label: t('songScreen.resetMix'), sub: t('songScreen.resetMixHint'), danger: true, onSelect: () => setMix(resetMix(store.get().mix)) }],
    });
  };

  const openMenu = (): void => {
    const s = store.get();                                           // снимок — только для подписей и галочек
    const id = myPartId(s);
    const sub = id ? nameOf(id) : t('songScreen.pickPartFirst');
    openActionSheet({
      title: t('songScreen.mixMenu'),
      items: [
        { label: t('songScreen.onlyMyPart'), sub, checked: s.myPartMode === 'only', disabled: !id, onSelect: () => applyMyPart(store.get().myPartMode === 'only' ? null : 'only') },
        { label: t('songScreen.bandWithoutMe'), sub, checked: s.myPartMode === 'without', disabled: !id, onSelect: () => applyMyPart(store.get().myPartMode === 'without' ? null : 'without') },
        { label: t('songScreen.changeMyPart'), onSelect: openPartPicker },
        { label: t('songScreen.resetMix'), danger: true, onSelect: confirmReset },
      ],
    });
  };

  return {
    tick() {
      let peak = 0;
      for (const [id, r] of rows) {
        const lvl = player.getTrackVuLevel(id);
        r.setVu(lvl);
        if (lvl > peak) peak = lvl;
      }
      master.setVu(peak);
    },
    openMenu,
    destroy() {
      closeActionSheet();                                            // открытый лист не должен пережить экран
      unsubStore();
      unsubPlayer();
      for (const r of rows.values()) r.destroy();
      master.destroy();
      root.remove();
    },
  };
}
